import type { Epic } from '../../index.ts'
import type { Action } from '@reduxjs/toolkit'
import { isAnyOf } from '@reduxjs/toolkit'
import { cubeStateKey, visualizationSlice } from '../../Slice/Visualization/VisualizationSlice.ts'
import type { SourceStatus } from '../../Slice/Visualization/VisualizationSliceTypes.ts'
import { getCubeTable } from '../../../Repository/Visualization/CubeTableRegistry.ts'
import { filter, mergeMap, of, withLatestFrom } from 'rxjs'

/**
 * Derives the terminal per-source `status` from the two independent readiness signals in the
 * cube world:
 *
 *   1. the source's own FE pipeline output — `snapshot` (buckets), produced by
 *      {@link blockingSourceEpic} via `bucketsReady`;
 *   2. the `(scopeId, blastType)` cube — tracked in `state.visualization.cubes`, decoded table
 *      held in the side `CubeTableRegistry`.
 *
 * ## Why this epic exists
 *
 * `KronaStreamingEpic` used to be the *only* place that dispatched
 * `sourceStatusChanged({ newStatus: 'dataReady' })` — it fired it when the last streaming batch
 * arrived. The cube migration removed that epic, and nothing replaced the terminal transition, so a
 * source got stuck at `'waiting'` (set by the `bucketsReady` reducer) forever:
 *
 *   - `SinkContainer` only renders its chart when `source.status === 'dataReady'` → otherwise an
 *     endless `<LoadingIndicator/>` (the "sinks never show data" symptom);
 *   - `isLoadingState('waiting') === true` → source panels show a perpetual "Waiting for data…"
 *     spinner (the "sources stuck loading" symptom).
 *
 * This epic restores that transition, now driven by the cube instead of streaming.
 *
 * ## Mapping
 *
 * For each source with a `snapshot` (its pipeline has produced buckets):
 *   - cube entry missing / `'loading'` → `'waiting'`   (still fetching the cube)
 *   - cube entry `'error'`             → `'error'`
 *   - cube entry `'ready'`             → `'dataReady'`  (render the sinks)
 *
 * Sources without a snapshot yet are left untouched (they're still assembling). We only dispatch
 * when the desired status actually differs from the current one, so identical re-triggers are
 * no-ops and there is no feedback loop (`sourceStatusChanged` is deliberately NOT a trigger).
 */
export const sourceReadyEpic: Epic = (action$, state$, { [visualizationSlice.name]: visualization }) => {
  const triggers = isAnyOf(
    visualization.bucketsReady,
    visualization.cubeRequested,
    visualization.cubeReady,
    visualization.cubeUnchanged,
    visualization.cubeError,
    visualization.updateSourceShared,
    visualization.setScope
  )

  return action$.pipe(
    filter((a: Action) => triggers(a)),
    withLatestFrom(state$),
    mergeMap(([, s]) => {
      const scopeId = s.visualization.scope?.id
      const out: Action[] = []

      for (const [sourceId, src] of Object.entries(s.visualization.sources)) {
        // No buckets yet → the source is still assembling; leave its status alone.
        if (!src?.snapshot) continue

        const blastType = src.shared?.blastType
        const spin = src.shared?.spin
        const cubeEntry =
          scopeId && blastType ? s.visualization.cubes[cubeStateKey(scopeId, blastType, spin)] : undefined

        let desired: SourceStatus
        if (!cubeEntry || cubeEntry.status === 'loading') {
          desired = 'waiting'
        } else if (cubeEntry.status === 'error') {
          desired = 'error'
        } else {
          desired = 'dataReady'
        }

        if (src.status === desired) continue

        if (import.meta.env.DEV) {
          const rows =
            scopeId && blastType && desired === 'dataReady'
              ? (getCubeTable(scopeId, blastType, spin)?.numRows ?? '?')
              : 'n/a'
          console.debug(
            `[viz] source ${sourceId}: ${src.status} → ${desired}`,
            { scopeId, blastType, cube: cubeEntry?.status ?? 'absent', rows }
          )
        }

        out.push(visualization.sourceStatusChanged({ sourceId, newStatus: desired }))
      }

      return of(...out)
    })
  )
}
